<template>
  <v-dialog
    scrollable
    width="750px"
    :value="open"
    @keydown.esc="$emit('close')"
    @click:outside="$emit('close')"
  >
    <v-card>
      <v-card-title class="px-8 py-6">
        {{ title }}
      </v-card-title>
      <v-divider class="ma-0" />
      <v-card-text
        class="px-8 py-4 mt-4 overflow-x-hidden"
        style="max-height: 600px;"
      >
        <Questions
          v-if="step === STEPS.QUESTIONS"
          :questions="questions"
          :isEditMode="isEditMode"
        />
        <Define
          v-if="step === STEPS.DEFINE_PROBLEM"
          :questions="questions"
          :problemDescription="problemDescription"
          :isEditMode="isEditMode"
          @input="updateProblem"
        />
      </v-card-text>

      <v-divider class="ma-0" />

      <v-card-actions class="px-8 py-6 d-flex justify-end">
        <template v-if="step === STEPS.QUESTIONS && isEditMode">
          <Button
            plain
            class="me-2"
            color="grey"
            data-tourid="problemSolving-5w2h-tool-cancel"
            :text="$t('common.button.cancel')"
            @click="$emit('close')"
          />

          <Button
            color="primary"
            :disabled="!canGoToNextStep"
            data-tourid="problemSolving-5w2h-tool-next"
            :text="$t('common.next')"
            @click="next"
          />
        </template>
        <template v-if="step === STEPS.DEFINE_PROBLEM && isEditMode">
          <Button
            plain
            color="grey"
            class="me-2"
            data-tourid="problemSolving-5w2h-tool-back"
            :text="$t('common.button.back')"
            @click="back"
          />
          <Button
            color="primary"
            data-tourid="problemSolving-5w2h-tool-finish"
            :text="$t('common.button.finish')"
            @click.native="saveProblem"
          />
        </template>
        <template v-if="!isEditMode">
          <Button
            plain
            color="grey"
            data-tourid="problemSolving-5w2h-tool-finish"
            :text="$t('common.button.close')"
            @click.native="$emit('close')"
          />
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import mixin from '@/mixins/mixin.vue'
import analysisToolImage from '@/assets/images/analysisTool.svg'
import Questions from '@/containers/ProblemSolvingView/DefineProblemTool/Questions.vue'
import Define from '@/containers/ProblemSolvingView/DefineProblemTool/Define.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import Button from '@/components/UI/Button'

const STEPS = {
  QUESTIONS: 0,
  DEFINE_PROBLEM: 1,
}

export default {
  name: 'DefineProblemTool',

  mixins: [mixin, trackerMixin],

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    problem: {
      type: String,
      default: '',
    },
    problemBreakdown: {
      type: Object,
      default: () => {},
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
  },

  components: {
    Questions,
    Define,
    Button,
  },

  data() {
    return {
      title: this.$t('problem_solving.problem_tool.5w2h_tool_name'),
      step: STEPS.QUESTIONS,
      problemDescription: this.problem,
      answers: '',
      analysisToolImage,
      STEPS,
      questions: [
        {
          key: 'what',
          label: this.$t('problem_solving.problem_tool.what_question'),
          placeholder: this.$t('problem_solving.problem_tool.what_placeholder'),
          answer: this.problemBreakdown?.what ?? '',
        },
        {
          key: 'why',
          label: this.$t('problem_solving.problem_tool.why_question'),
          placeholder: this.$t('problem_solving.problem_tool.why_placeholder'),
          answer: this.problemBreakdown?.why ?? '',
        },
        {
          key: 'where',
          label: this.$t('problem_solving.problem_tool.where_question'),
          placeholder: this.$t(
            'problem_solving.problem_tool.where_placeholder'
          ),
          answer: this.problemBreakdown?.where ?? '',
        },
        {
          key: 'when',
          label: this.$t('problem_solving.problem_tool.when_question'),
          placeholder: this.$t('problem_solving.problem_tool.when_placeholder'),
          answer: this.problemBreakdown?.when ?? '',
        },
        {
          key: 'who',
          label: this.$t('problem_solving.problem_tool.who_question'),
          placeholder: this.$t('problem_solving.problem_tool.who_placeholder'),
          answer: this.problemBreakdown?.who ?? '',
        },
        {
          key: 'how',
          label: this.$t('problem_solving.problem_tool.how_question'),
          placeholder: this.$t('problem_solving.problem_tool.how_placeholder'),
          answer: this.problemBreakdown?.how ?? '',
        },
        {
          key: 'howMuch',
          label: this.$t('problem_solving.problem_tool.how_much'),
          placeholder: this.$t(
            'problem_solving.problem_tool.how_much_placeholder'
          ),
          answer: this.problemBreakdown?.howMuch ?? '',
        },
      ],
    }
  },

  mounted() {
    this.step = this.isEditMode ? STEPS.QUESTIONS : STEPS.DEFINE_PROBLEM
  },

  computed: {
    canGoToNextStep() {
      return this.questions
        .map(question => Boolean(question?.answer?.trim()))
        .includes(true)
    },
  },

  methods: {
    next() {
      this.step = STEPS.DEFINE_PROBLEM
      this.title = this.$t('common.summary')
      this.updateProblemBreakdown()
      this.trackTool('5W2H', 'next', 'problem-solving')
    },

    back() {
      this.step = STEPS.QUESTIONS
      this.title = this.$t('problem_solving.problem_tool.5w2h_tool_name')
      this.trackTool('5W2H', 'back', 'problem-solving')
    },

    updateProblemBreakdown() {
      const problemBreakdown = this.questions.reduce(
        (obj, item) => ((obj[item.key] = item.answer), obj),
        {}
      )
      this.$emit('setProblemBreakdown', problemBreakdown)
    },

    saveProblem() {
      this.$emit('updateProblem', this.problemDescription)
      this.$emit('close')
      this.trackTool('5W2H', 'done', 'problem-solving')
    },

    updateProblem(problem) {
      this.problemDescription = problem
    },
  },
}
</script>
